import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { alpha, type Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { testIdPart } from '@/lib/testIds';

import { NAV_SECTIONS, type NavSection } from '../navigation';

/** Is this section the one the person is currently inside? */
function isCurrentSection(section: NavSection, pathname: string) {
  return pathname === section.path || pathname.startsWith(section.path + '/');
}

/*
 * The current entry, as Part 2 of the wireframes draws it. A section with no
 * sub-pages to open (Dashboard, Help) has its box filled light blue (page 1); a
 * section that opens keeps a plain box, and its current sub-page is written in
 * blue instead (page 4). Folded, the sub-pages are hidden, so the current
 * section's box is filled whatever it holds.
 *
 * Body text on the fill measures 11.5:1 and the primary blue on white 5.68:1
 * (both need 4.5). aria-current, not the colour, is what names the page.
 */
const filledWhenCurrent = (theme: Theme) => ({
  '&.Mui-selected': { backgroundColor: alpha(theme.palette.primary.main, 0.3) },
  '&.Mui-selected:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.38) },
});

const plainWhenCurrent = (theme: Theme) => ({
  '&.Mui-selected': { backgroundColor: 'transparent' },
  '&.Mui-selected:hover': { backgroundColor: theme.palette.action.hover },
});

interface SideNavProps {
  /** Called when a destination is chosen, so the drawer can close itself. */
  onNavigate?: () => void;
  /** Which sections to show. Activist staff get a different list. */
  sections?: NavSection[];
  /** Names the region, so two navigations can be told apart. */
  label?: string;
  /**
   * Icons only, each named in a tooltip: the desktop navigation folded out of
   * the way. Sub-pages and the New action are left out while folded.
   */
  folded?: boolean;
  /**
   * Names the navigation for the testing team's tests. Each link is
   * `<testId>-<its path>-link`, the path written with dashes, and a page
   * inside a section `<testId>-<its path>-subpage-link`, because a section and
   * its first page share an address. Each section's add button is
   * `<testId>-<its path>-button`. See docs/test-ids.md.
   */
  testId: string;
}

/**
 * The twelve sections of the portal.
 *
 * Only the section being viewed shows its sub-pages, which is how the
 * wireframes behave. Rendering is driven entirely by NAV_SECTIONS, so a new
 * section is a data change rather than a markup change.
 */
export function SideNav({
  onNavigate,
  sections = NAV_SECTIONS,
  label = 'Sections',
  folded = false,
  testId,
}: SideNavProps) {
  const { pathname } = useLocation();

  return (
    // A navigation landmark holding a real list. `List component="nav"` would
    // draw the <nav> in place of the <ul>, leaving every item a list item with
    // no list, which a screen reader cannot count.
    <Box component="nav" aria-label={label} data-testid={testId}>
      <List sx={{ py: 1 }}>
        {sections.map((section) => {
          const current = isCurrentSection(section, pathname);
          const opens = !folded && Boolean(section.children || section.action);
          const Icon = section.icon;

          return (
            <Box key={section.path} component="li" sx={{ listStyle: 'none' }}>
              {/* Folded, the name is the tooltip and the link's accessible
                name, so it is read out and shown on hover or focus while
                only the icon is drawn. */}
              <Tooltip title={folded ? section.label : ''} placement="right">
                <ListItemButton
                  component={RouterLink}
                  to={section.path}
                  onClick={onNavigate}
                  selected={current}
                  // aria-current is what tells a screen reader which page this is.
                  // The highlight alone would leave that information to colour.
                  aria-current={pathname === section.path ? 'page' : undefined}
                  aria-label={folded ? section.label : undefined}
                  data-testid={`${testId}-${testIdPart(section.path)}-link`}
                  sx={[
                    opens ? plainWhenCurrent : filledWhenCurrent,
                    folded ? { justifyContent: 'center', px: 0 } : null,
                  ]}
                >
                  <ListItemIcon sx={{ minWidth: folded ? 0 : 40 }}>
                    <Icon />
                  </ListItemIcon>
                  {folded ? null : (
                    <ListItemText
                      primary={section.label}
                      slotProps={{ primary: { sx: { fontWeight: current ? 600 : 400 } } }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>

              {!folded && current && (section.children || section.action) ? (
                <Box sx={{ pb: 1 }}>
                  <List disablePadding>
                    {section.children?.map((child) => (
                      <Box key={child.path} component="li" sx={{ listStyle: 'none' }}>
                        <ListItemButton
                          component={RouterLink}
                          to={child.path}
                          onClick={onNavigate}
                          selected={pathname === child.path}
                          aria-current={pathname === child.path ? 'page' : undefined}
                          data-testid={`${testId}-${testIdPart(child.path)}-subpage-link`}
                          sx={[plainWhenCurrent, { pl: 7, py: 0.5 }]}
                        >
                          <ListItemText
                            primary={child.label}
                            slotProps={{
                              primary: {
                                sx: {
                                  fontSize: 14,
                                  color:
                                    pathname === child.path ? 'primary.main' : undefined,
                                },
                              },
                            }}
                          />
                        </ListItemButton>
                      </Box>
                    ))}
                  </List>

                  {section.action ? (
                    <Box sx={{ px: 2, pt: 1 }}>
                      <Button
                        component={RouterLink}
                        to={section.action.path}
                        onClick={onNavigate}
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        fullWidth
                        data-testid={`${testId}-${testIdPart(section.action.path)}-button`}
                      >
                        {section.action.label}
                      </Button>
                    </Box>
                  ) : null}
                </Box>
              ) : null}
            </Box>
          );
        })}
      </List>
    </Box>
  );
}
